import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { formatToCompactNumber } from "app/utils/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

interface BenefitValue {
  actual: number;
  projected: number;
}

export interface ProtectRow {
  id: string;
  name: string;
  trueIQProtectScore: number;
  profilePicture?: string | null;
}

export interface ProtectYearData {
  planYear: number;
  year: number;
  insuredAge: number | null;
  combinedTrueIQIndex: number;
  deathBenefit: BenefitValue;
  livingBenefit: BenefitValue;
  disability: BenefitValue;
  shareholderValue?: BenefitValue; // BS
  keyPersonValue?: BenefitValue; // KP
  rows: ProtectRow[];
}

interface ProtectChartProps {
  data: ProtectYearData[];
  isDark?: boolean;
  /** Which benefits to include. Defaults to all three when omitted. */
  selected?: Set<BenefitKey>;
  /** When false, hide TrueIQ hexagon badges (projected years). */
  showProjections?: boolean;
  /** X-axis dimension: "year" | "planYear" | "insuredAge". Defaults to "year". */
  xAxisDimension?: string;
  /** Show a loading overlay (e.g. while year is changing). */
  loading?: boolean;
}

type BenefitKey = "death" | "living" | "disability";

const GREEN = "#1EA97C";
const RED = "#D9342B";

const fmtMoney = (v: number) => `$${formatToCompactNumber(v, { decimals: 1 })}`;

/** Inline TrueIQ logo SVG — avoids renderToStaticMarkup + useContext crash */
const trueIqSvgUrl = (color: string) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 54 49" fill="none"><path d="M31.6871 44.3324C30.7013 42.9642 29.1175 42.1548 27.4314 42.1548H18.4944C17.0884 42.1422 15.7693 41.379 15.0523 40.1663L6.87847 26.2902C6.13208 25.0257 6.13208 23.4545 6.87847 22.19L13.5974 10.7841H6.26091L1.4311 18.9818C-0.478985 22.225 -0.478985 26.2552 1.4311 29.4984L9.60636 43.3759C11.431 46.4707 14.7821 48.4186 18.367 48.4732C18.4426 48.476 18.5 48.476 18.5322 48.476H34.6727L31.6857 44.3324H31.6871ZM52.2387 18.9804L44.0634 5.10289C42.2094 1.95489 38.7897 0 35.1376 0H26.4414L22.6941 6.32119H35.1376C36.5618 6.32119 37.8949 7.08438 38.6189 8.31109L46.7941 22.1872C47.5391 23.4517 47.5391 25.0229 46.7941 26.2874L40.9365 35.2259L44.611 41.4448L52.2401 29.4956C54.1516 26.2524 54.1516 22.2222 52.2401 18.979L52.2387 18.9804ZM9.60776 5.10289L8.88938 6.32119H16.216L19.92 0H18.535C14.8829 0 11.4632 1.95489 9.60776 5.10289Z" fill="${color}"/><path d="M36.7396 22.8095L36.4133 22.2451L36.6556 22.6554C36.685 22.7058 36.7144 22.7576 36.7396 22.8095Z" fill="${color}"/><path d="M48.5281 46.5617H44.2304C43.1298 46.5617 42.0921 46.0267 41.4535 45.1305L33.5892 34.1055C32.5081 32.5904 30.752 31.6857 28.891 31.6843C26.6532 31.6843 23.4128 31.6843 23.2223 31.6843C22.7238 31.6773 22.2575 31.4071 22.004 30.9757L18.4654 24.9696C18.1993 24.5187 18.1993 23.96 18.464 23.5105L22.004 17.5015C22.2617 17.0646 22.7364 16.793 23.242 16.793H30.4314C30.9369 16.793 31.4116 17.0646 31.6693 17.5015L34.9629 23.0932L48.5281 46.5631V46.5617Z" fill="${color}"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const ALL_SELECTED = new Set<BenefitKey>(["death", "living", "disability"]);

export function ProtectChart({
  data,
  isDark = false,
  selected: selectedProp,
  showProjections = true,
  xAxisDimension = "year",
  loading = false,
}: ProtectChartProps) {
  // Checkboxes are managed by the parent (value filters). ProtectChart is display-only.
  const selected = selectedProp ?? ALL_SELECTED;

  const { xLabels, baseVals, benefitVals } = useMemo(() => {
    const n = selected.size;
    // Always use projected — protect chart is a coverage/projection view.
    // Using actual causes issues for KP where actual is a small partial payment
    // (e.g. 345) vs the meaningful projected value (35113).
    const val = (v: BenefitValue | undefined) => v?.projected ?? 0;
    // KP uses keyPersonValue as base; BS uses shareholderValue
    const baseOf = (d: ProtectYearData) => val(d.keyPersonValue ?? d.shareholderValue);
    // X-axis label respects the chart filter (year / plan year / insured age).
    const labelOf = (d: ProtectYearData) => {
      if (xAxisDimension === "planYear") return String(d.planYear);
      if (xAxisDimension === "insuredAge") return d.insuredAge == null ? "" : `Age ${d.insuredAge}`;
      return String(d.year);
    };
    return {
      xLabels: data.map(labelOf),
      baseVals: data.map((d) => baseOf(d) * n),
      benefitVals: data.map((d) => {
        let s = 0;
        if (selected.has("death")) s += val(d.deathBenefit);
        if (selected.has("living")) s += val(d.livingBenefit);
        if (selected.has("disability")) s += val(d.disability);
        return s;
      }),
    };
  }, [data, selected, xAxisDimension]);

  const option = useMemo(() => {
    const textColor = isDark ? "#94a3b8" : "#64748b";
    const gridColor = isDark ? "#334155" : "#e2e8f0";
    const grayFill = isDark ? "rgba(100,116,139,0.22)" : "rgba(203,213,225,0.55)";
    const grayLine = isDark ? "#64748b" : "#94a3b8";

    // ── Build extended arrays with interpolated crossing points ─────────────
    // At each sign-change between adjacent years, compute the exact Y where
    // benefit == base and insert it so the color switch happens at that pixel.
    const extX: string[] = [];
    const extBase: number[] = [];
    const extGreenFloor: number[] = [];
    const extGreenDelta: number[] = [];
    const extRedFloor: number[] = [];
    const extRedDelta: number[] = [];
    const extGreenLine: (number | null)[] = [];
    const extRedLine: (number | null)[] = [];

    for (let i = 0; i < benefitVals.length; i++) {
      if (i > 0) {
        const prevAbove = benefitVals[i - 1] >= baseVals[i - 1];
        const currAbove = benefitVals[i] >= baseVals[i];
        if (prevAbove !== currAbove) {
          // Linear-interpolate the exact crossing point
          const dBen = benefitVals[i] - benefitVals[i - 1];
          const dBase = baseVals[i] - baseVals[i - 1];
          const t = (baseVals[i - 1] - benefitVals[i - 1]) / (dBen - dBase);
          const cross = benefitVals[i - 1] + t * dBen; // ≈ base at crossing
          // Insert the crossing point (empty label — not rendered on x-axis)
          extX.push("");
          extBase.push(cross);
          extGreenFloor.push(cross);
          extGreenDelta.push(0); // benefit == base → delta = 0
          extRedFloor.push(cross);
          extRedDelta.push(0);
          extGreenLine.push(cross); // belongs to both lines
          extRedLine.push(cross);
        }
      }
      const b = benefitVals[i];
      const bs = baseVals[i];
      const above = b >= bs;
      extX.push(xLabels[i]);
      extBase.push(bs);
      extGreenFloor.push(bs);
      extGreenDelta.push(Math.max(0, b - bs));
      extRedFloor.push(Math.min(b, bs));
      extRedDelta.push(Math.max(0, bs - b));
      extGreenLine.push(above ? b : null);
      extRedLine.push(above ? null : b);
    }

    // Badges hidden when projections are off (projected-only years have no confirmed index)
    const badges = !showProjections
      ? []
      : data.map((d, i) => {
          const idx = d.combinedTrueIQIndex;
          const good = idx >= 100;
          const color = good ? "#1DA750" : RED;
          return {
            coord: [xLabels[i], benefitVals[i]], // use label so index offset doesn't matter
            symbol: `image://${trueIqSvgUrl(color)}`,
            symbolSize: [28, 26],
            symbolOffset: [0, -38],
            label: {
              show: true,
              position: "bottom" as const,
              distance: 3,
              formatter: idx > 0 ? String(idx.toFixed(1)) : "—",
              color,
              fontSize: 11,
              fontWeight: "600",
              fontFamily: "Conthrax Semi-Bold, sans-serif",
            },
          };
        }) ?? [];

    return {
      animation: true,
      tooltip: { show: false },
      legend: { show: false },
      grid: { left: 68, right: 20, top: 54, bottom: 64 },
      dataZoom: [
        {
          type: "slider" as const,
          show: true,
          bottom: 4,
          height: 20,
          borderColor: "transparent",
          fillerColor: isDark ? "rgba(167,183,204,0.3)" : "rgba(167,183,204,0.4)",
          handleStyle: {
            color: isDark ? "#ffffff" : "#334155",
            borderColor: isDark ? "#4A4A60" : "#E5E7EB",
          },
          textStyle: { color: textColor, fontSize: 10 },
          showDetail: false,
          showDataShadow: false,
          realtime: true,
          xAxisIndex: 0,
        },
      ],
      xAxis: {
        type: "category" as const,
        data: extX,
        boundaryGap: false,
        axisLine: { lineStyle: { color: gridColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, fontSize: 11 },
      },
      yAxis: {
        type: "value" as const,
        min: 0,
        axisLabel: { color: textColor, fontSize: 10, formatter: fmtMoney },
        splitLine: { lineStyle: { color: gridColor, type: "dashed" as const } },
      },
      series: [
        // Gray base (0 → shareholderValue × n)
        {
          type: "line" as const,
          name: "__base__",
          data: extBase,
          z: 1,
          symbol: "none",
          lineStyle: { color: grayLine, width: 1.5 },
          areaStyle: { color: grayFill, origin: "start" as const },
        },
        // Green fill stack
        {
          type: "line" as const,
          name: "__gf__",
          data: extGreenFloor,
          stack: "gb",
          z: 2,
          symbol: "none",
          lineStyle: { opacity: 0 },
          areaStyle: { color: "transparent" },
        },
        {
          type: "line" as const,
          name: "__gd__",
          data: extGreenDelta,
          stack: "gb",
          z: 2,
          symbol: "none",
          lineStyle: { opacity: 0 },
          areaStyle: { color: "rgba(34,197,94,0.35)" },
        },
        // Red fill stack
        {
          type: "line" as const,
          name: "__rf__",
          data: extRedFloor,
          stack: "rb",
          z: 2,
          symbol: "none",
          lineStyle: { opacity: 0 },
          areaStyle: { color: "transparent" },
        },
        {
          type: "line" as const,
          name: "__rd__",
          data: extRedDelta,
          stack: "rb",
          z: 2,
          symbol: "none",
          lineStyle: { opacity: 0 },
          areaStyle: { color: "rgba(239,68,68,0.35)" },
        },
        // Benefit line — green portion
        {
          type: "line" as const,
          name: "__bg__",
          data: extGreenLine,
          z: 4,
          connectNulls: false,
          symbol: "none",
          lineStyle: { color: GREEN, width: 2 },
          markPoint: { silent: true, data: badges },
        },
        // Benefit line — red portion
        {
          type: "line" as const,
          name: "__br__",
          data: extRedLine,
          z: 4,
          connectNulls: false,
          symbol: "none",
          lineStyle: { color: RED, width: 2 },
        },
      ],
    };
  }, [xLabels, baseVals, benefitVals, data, isDark, showProjections]);

  return (
    <div className="w-full">
      <div className="relative">
        {loading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-xl"
            style={{ background: isDark ? "rgba(30,32,48,0.7)" : "rgba(255,255,255,0.7)" }}
          >
            <svg
              className="animate-spin h-8 w-8 text-[#1581ED]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </div>
        )}
        <ReactECharts option={option} style={{ height: 360 }} notMerge lazyUpdate={false} />
      </div>
    </div>
  );
}
